// A page's own heading and words — About, Privacy, the introduction over a
// list — as the admin last wrote them. The admin edits them here, in place;
// Claude Desktop edits the same row with save_page.
import type { SitePageId } from "@bibleunleashed/shared";
import { useEffect, useRef, useState } from "react";
import { Prose } from "@/commentary/Prose";
import { useUser } from "@/lib/auth";
import { usePage, useSavePage } from "@/lib/pages";
import { Field } from "./Field";
import { Icon } from "./Icon";

export function PageText({ id, titleClassName, bodyClassName }: { id: SitePageId; titleClassName: string; bodyClassName: string }) {
  const { data: user } = useUser();
  const page = usePage(id);
  const save = useSavePage(id);
  const [draft, setDraft] = useState<{ title: string; bodyMd: string } | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);

  // Grow with the text, so the editor keeps roughly the height of what it edits.
  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [draft?.bodyMd]);

  if (!page) return null;

  if (draft) {
    const doSave = async () => {
      await save.mutateAsync(draft);
      setDraft(null);
    };
    return (
      <div className="space-y-3">
        <Field label="Title" value={draft.title} onChange={(title) => setDraft({ ...draft, title })} />
        <label className="block">
          <span className="mb-0.5 block font-sans text-[11px] tracking-[0.06em] text-ink-3 uppercase">Text (markdown)</span>
          <textarea
            ref={area}
            value={draft.bodyMd}
            onChange={(e) => setDraft({ ...draft, bodyMd: e.target.value })}
            rows={3}
            className="block w-full resize-none rounded-md border border-rule bg-paper-2 px-3 py-2 font-serif text-[16px] leading-relaxed text-ink focus:border-accent focus-visible:outline-none"
          />
        </label>
        <div className="flex items-center gap-2">
          {save.error && <span className="text-[12px] text-accent">{save.error.message}</span>}
          <span className="ml-auto flex gap-2">
            <button
              onClick={() => setDraft(null)}
              className="rounded-md border border-rule px-3 py-1.5 text-[13px] text-ink-2 hover:text-ink"
            >
              Cancel
            </button>
            <button
              onClick={() => void doSave()}
              disabled={save.isPending}
              className="rounded-md bg-accent px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <h1 className={titleClassName}>
        {page.title}
        {user?.role === "admin" && (
          <button
            onClick={() => {
              save.reset();
              setDraft({ title: page.title, bodyMd: page.bodyMd });
            }}
            aria-label="Edit this page's text"
            className="ml-2 inline-grid size-7 place-items-center rounded align-middle text-ink-3 hover:bg-paper-3 hover:text-ink"
          >
            <Icon name="pencil" width="15" height="15" />
          </button>
        )}
      </h1>
      {page.bodyMd.trim() && <Prose md={page.bodyMd} className={`page-text ${bodyClassName}`} />}
    </>
  );
}
